import { useCallback, useRef, useState } from 'react'
import { Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import useDirectUpload from '@/hooks/useDirectUpload'

type ImageUploadProps = {
  onUpload: (signedId: string) => void
  onPreviewChange?: (url: string | null) => void
  onUploadingChange?: (uploading: boolean) => void
  currentUrl?: string | null
  className?: string
  variant?: 'default' | 'circle'
}

export default function ImageUpload({ onUpload, onPreviewChange, onUploadingChange, currentUrl, className = '', variant = 'default' }: ImageUploadProps) {
  const { upload, progress, uploading, error } = useDirectUpload()
  const [preview, setPreview] = useState<string | null>(currentUrl ?? null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return

    const blobUrl = URL.createObjectURL(file)
    setPreview(blobUrl)
    onPreviewChange?.(blobUrl)
    onUploadingChange?.(true)

    try {
      const signedId = await upload(file)
      onUpload(signedId)
    } catch {
      setPreview(currentUrl ?? null)
      onPreviewChange?.(currentUrl ?? null)
    } finally {
      onUploadingChange?.(false)
    }
  }, [upload, onUpload, onPreviewChange, onUploadingChange, currentUrl])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const clear = () => {
    setPreview(null)
    onPreviewChange?.(null)
    onUpload('')
    if (inputRef.current) inputRef.current.value = ''
  }

  const isCircle = variant === 'circle'

  return (
    <div className={`${className} ${isCircle ? 'relative' : ''}`}>
      <div
        className={`relative flex cursor-pointer items-center justify-center ring-1 ring-black/10 bg-muted/30 hover:bg-muted/50 ${isCircle ? 'aspect-square rounded-full overflow-hidden' : 'min-h-32 rounded-lg'}`}
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            className={`h-full w-full object-cover outline-1 -outline-offset-1 outline-black/5 ${isCircle ? '' : 'max-h-48 rounded-lg'}`}
          />
        ) : (
          <div className={`flex flex-col items-center gap-1 text-muted-foreground ${isCircle ? 'p-2' : 'px-3 py-6'}`}>
            <Upload className={isCircle ? 'size-4 shrink-0' : 'size-5 shrink-0'} />
            {!isCircle && <span className="text-xs text-center leading-tight">Click or drop an image</span>}
          </div>
        )}

        {!isCircle && preview && (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6"
            onClick={(e) => {
              e.stopPropagation()
              clear()
            }}
          >
            <X className="size-3" />
          </Button>
        )}

        {uploading && (
          <div className={`absolute inset-x-0 bottom-0 bg-black/50 px-3 py-1.5 ${isCircle ? 'rounded-b-full' : 'rounded-b-lg'}`}>
            <div className="h-1 rounded-full bg-white/30">
              <div
                className="h-1 rounded-full bg-white transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {isCircle && preview && (
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="absolute top-0 right-0 h-6 w-6 rounded-full shadow-sm ring-1 ring-black/10"
          onClick={(e) => {
            e.stopPropagation()
            clear()
          }}
        >
          <X className="size-3" />
        </Button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />

      {error && <p className="mt-1 text-base text-destructive">{error}</p>}
    </div>
  )
}
